import { actionChannel, all, take, takeEvery, takeLatest, put, select  } from 'redux-saga/effects'
import { Channel } from 'redux-saga'
import * as Eff from 'redux-saga/effects' // tslint:disable-line:no-duplicate-imports
import { IAction, ISuccessResponse } from '@Root/types'
import router from '@Site/Router'
import map from 'lodash/map'

import toastr from 'toastr'
import Logger from '@Utils/Logger'
import S3Remover from '@Utils/S3Remover'

import {
  ISubmitUrlsPayload,
  ElementActionTypes,
  ISubmitUrlsSuccessResponseBody,
  ICreateElementSuccessResponseBody,
  ICreateElementPayload,
  IFetchElementsPayload,
  IDeleteElementPayload,
  IFetchElementsSuccessResponseBody,
} from './types'

import {
  submitUrlsSuccess,
  submitUrlsFailure,
  createElementSuccess,
  createElementFailure,
  fetchElementsSuccess,
  fetchElementsFailure,
  deleteElementSuccess,
  deleteElementFailure,
  deleteElement,
} from './actions'

import {
  fetchProjects,
  deleteProjects,
} from '@SiteModules/Project/actions'

import {
  setValuesAfterUpload,
  addProjectIdPendingCleanup,
  addElementsForProjectVersions,
  removeFromElementsForProjectVersions,
  setSizeLimitsAlertAtSubmitUrl,
  removeFromSubmittingUrls,
  resetSubmittingUrls,
} from '@SiteModules/ProjectVersionSettings/actions'

import {
  openSetupProjectPopup,
} from '@SiteModules/Popup/actions'

import {
  startQuickstartProcessing,
} from '@SiteModules/Onboarding/actions'

import {
  getCurrentAccountMaxFileSize,
} from '@SiteModules/Account/selectors'


import * as API from './api'

const call: any = Eff.call
const MAX_CONCURRENT_ELEMENT_CREATIONS = 2

export function* createElementSaga(action: IAction<ICreateElementPayload>): Generator<any, any, any> {
  try {
    const response: ISuccessResponse<ICreateElementSuccessResponseBody> = yield call(API.createElement, action.payload)
    const payloadSuccess = response.data
    yield put(createElementSuccess(payloadSuccess))

    const state = yield select()
    const { elementId, startQuickstartProcessingPayloadData } = action.payload
    const projectVersionId = response.data.data.id
    const projectId = response.data.data.attributes.projectId

    if (startQuickstartProcessingPayloadData) {
      yield(put(startQuickstartProcessing({ ...startQuickstartProcessingPayloadData, projectVersionId })))
    } else {
      const shouldKeepElementAndProjectVersion = !action.payload.discardAfterCreate &&
        map(state.projectVersionSettings.elementsForProjectVersions, elementForProjectVersion => elementForProjectVersion.elementId).includes(elementId)

      if (shouldKeepElementAndProjectVersion) {
        yield put(setValuesAfterUpload({
          elementId: action.payload.elementId,
          projectVersionId,
          projectId,
          duration: payloadSuccess.data.attributes.unformattedDuration
        }))
      } else {
        yield put(deleteElement({ elementId }))
        const setupPopupHasClosed = !state.popup.openSetupProjectPopup
        if (action.payload.discardAfterCreate || setupPopupHasClosed) {
          yield put(deleteProjects({ projectIds: [projectId] }))
        } else {
          yield put(addProjectIdPendingCleanup({ projectId }))
        }
      }
    }
  } catch (error) {
    Logger.error(error, 'Element(s) could not be created!')

    try {
      const remover = new S3Remover({ fileUrl: action.payload.url })
      yield call(() => remover.call())
    } catch (cleanupError) {
      Logger.error(cleanupError, 'Uploaded file could not be cleaned up after element creation failed!')
    }

    yield put(removeFromElementsForProjectVersions({ elementId: action.payload.elementId }))
    yield put(createElementFailure())
  }
}

export function* createElementWorker(createElementRequests: Channel<IAction<ICreateElementPayload>>): Generator<any, any, any> {
  while (true) {
    const action: IAction<ICreateElementPayload> = yield take(createElementRequests)
    yield call(createElementSaga, action)
  }
}

function* submitUrlsSaga(action: IAction<ISubmitUrlsPayload>): Generator<any, any, any> {
  try {
    const { payload } = action

    if (payload.submittedFromChannelsPage) {
      router.navigate('/')
      yield put(openSetupProjectPopup({ projectVersionIds: [], projectType: 'subtitles' }))
    }

    const response: ISuccessResponse<ISubmitUrlsSuccessResponseBody> = yield call(API.submitUrls, payload)
    const payloadSuccessResponse = response.data
    yield put(submitUrlsSuccess(payloadSuccessResponse))

    const projectVersionId = payloadSuccessResponse.projectVersions.data[0].id
    const projectId = payloadSuccessResponse.projectVersions.data[0].attributes.projectId
    const elementId = payloadSuccessResponse.elements.data[0].id

    const state = yield select()
    const submitUrlIdentifiers = map(state.projectVersionSettings.submitUrl.submittingUrls, submittingUrl => submittingUrl.identifier)

    if (submitUrlIdentifiers.includes(payload.submitUrlIdentifier)) {
      yield put(removeFromSubmittingUrls({ submitUrlIdentifier: payload.submitUrlIdentifier }))
      const duration = payloadSuccessResponse.elements.data[0].attributes.unformattedDuration
      const name = payloadSuccessResponse.elements.data[0].attributes.title
      const size = payloadSuccessResponse.elements.data[0].attributes.unformattedSize
      const thumbnailUrl = payloadSuccessResponse.elements.data[0].attributes.thumbnailUrl
      const maxFileSize = getCurrentAccountMaxFileSize(state)

      if (size <= maxFileSize) {
        const isDownloadingToDetermineDuration = duration === 0
        yield put(addElementsForProjectVersions({
          elementsToAdd: [{
            elementId,
            name,
            size,
            projectVersionId,
            projectId,
            duration,
            downloading: isDownloadingToDetermineDuration,
            thumbnailUrl
          }]
        }))
      } else {
        yield put(setSizeLimitsAlertAtSubmitUrl())

        yield put(deleteElement({ elementId }))
        yield put(deleteProjects({ projectIds: [projectId] }))
      }
    } else {
      yield put(deleteElement({ elementId }))
      yield put(deleteProjects({ projectIds: [projectId] }))
    }
  } catch (e) {
    Logger.error(e, 'URL(s) could not be submitted')
    yield(put(resetSubmittingUrls()))
    yield put(submitUrlsFailure())
  }
}

function* fetchElementsSaga(action: IAction<IFetchElementsPayload>) {
  try {
    const response: ISuccessResponse<IFetchElementsSuccessResponseBody> = yield call(API.fetchElements, action.payload)

    yield put(fetchElementsSuccess(response.data))
  } catch (e) {
    Logger.error(e, 'Assets could not be loaded')
    yield put(fetchElementsFailure())
  }
}

function* deleteElementSaga(action: IAction<IDeleteElementPayload>): Generator<any, any, any> {
  try {
    yield call(API.deleteElement, action.payload)

    yield put(deleteElementSuccess({ elementId: action.payload.elementId }))

    if (action.payload.notifyAboutSuccess) {
      toastr.success('', 'Asset deleted')
    }

    const state = yield select()
    const searchText = state.projects.searchText
    const payloadFetchProjects = {
      selectedLabels: state.labels.selectedLabelIds,
      workspaceId: state.workspaces.currentWorkspaceId,
      page: 1,
      ...(searchText.length > 0 ? { searchText } : {}),
    }

    yield put(fetchProjects(payloadFetchProjects)) // To get updated durations and assets
  } catch (e) {
    Logger.error(e, 'Asset could not be deleted')
    yield put(deleteElementFailure())
  }
}

function* elementSagas() {
  const createElementRequests: Channel<IAction<ICreateElementPayload>> = yield actionChannel(ElementActionTypes.CREATE_ELEMENT)
  const createElementWorkers = Array.from(
    { length: MAX_CONCURRENT_ELEMENT_CREATIONS },
    () => call(createElementWorker, createElementRequests),
  )

  yield all([
    ...createElementWorkers,
    takeEvery(ElementActionTypes.SUBMIT_URLS, submitUrlsSaga),
    takeLatest(ElementActionTypes.FETCH_ELEMENTS, fetchElementsSaga),
    takeEvery(ElementActionTypes.DELETE_ELEMENT, deleteElementSaga),
  ])
}

export default elementSagas
